import type {
  ParsedRepoMarkdownUrl,
  RepoMarkdownError,
  RepoMarkdownProvider,
  RepoMarkdownResult,
} from "./types";

const MARKDOWN_EXTENSIONS = new Set([
  ".md",
  ".markdown",
  ".mdown",
  ".mkd",
  ".mdx",
]);

const MAX_OWNER_LEN = 100;
const MAX_REPO_LEN = 100;
const MAX_REF_LEN = 200;
const MAX_PATH_LEN = 512;
const MAX_PATH_SEGMENTS = 20;

const githubBrowseHosts = new Set(["github.com", "www.github.com"]);
const githubRawHosts = new Set(["raw.githubusercontent.com"]);
const gitlabHosts = new Set(["gitlab.com", "www.gitlab.com"]);
const bitbucketHosts = new Set(["bitbucket.org", "www.bitbucket.org"]);
const codebergHosts = new Set(["codeberg.org", "www.codeberg.org"]);
const giteaHosts = new Set(["gitea.com", "www.gitea.com"]);
const sourcehutHosts = new Set(["git.sr.ht"]);
const huggingfaceHosts = new Set(["huggingface.co", "www.huggingface.co"]);
const giteeHosts = new Set(["gitee.com", "www.gitee.com"]);
const azureHosts = new Set(["dev.azure.com"]);

function policyError(
  code: RepoMarkdownError["code"],
  message: string,
): RepoMarkdownError {
  return { code, message };
}

function normalizeHostname(hostname: string) {
  return hostname.replace(/\.$/, "").toLowerCase();
}

function isValidSegment(segment: string, maxLen: number): boolean {
  return (
    segment.length > 0 &&
    segment.length <= maxLen &&
    !segment.includes("..") &&
    !segment.includes("\\") &&
    !segment.includes("/")
  );
}

function stripGitSuffix(name: string) {
  return name.replace(/\.git$/i, "");
}

function hasMarkdownExtension(path: string): boolean {
  const lower = path.toLowerCase();
  const slash = lower.lastIndexOf("/");
  const base = slash >= 0 ? lower.slice(slash + 1) : lower;
  const dot = base.lastIndexOf(".");
  if (dot < 0) {
    return false;
  }
  return MARKDOWN_EXTENSIONS.has(base.slice(dot));
}

function validateFilePath(path: string): string | null {
  if (path.length === 0 || path.length > MAX_PATH_LEN) {
    return null;
  }
  if (path.startsWith("/") || path.includes("\\") || path.includes("..")) {
    return null;
  }
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0 || segments.length > MAX_PATH_SEGMENTS) {
    return null;
  }
  for (const segment of segments) {
    if (!isValidSegment(segment, MAX_PATH_LEN)) {
      return null;
    }
  }
  if (!hasMarkdownExtension(path)) {
    return null;
  }
  return segments.join("/");
}

function joinPath(segments: string[]): string {
  return segments.join("/");
}

function parsed(
  provider: RepoMarkdownProvider,
  owner: string,
  repo: string,
  options: {
    projectPath?: string;
    hubKind?: "datasets" | "spaces" | null;
    ref?: string | null;
    filePath?: string | null;
  } = {},
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (!isValidSegment(owner, MAX_OWNER_LEN) || !isValidSegment(repo, MAX_REPO_LEN)) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "Repository owner or name is invalid."),
    };
  }

  const ref = options.ref ?? null;
  if (ref !== null && !isValidSegment(ref, MAX_REF_LEN)) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "Branch, tag, or commit ref is invalid."),
    };
  }

  let filePath = options.filePath ?? null;
  if (filePath !== null) {
    const validated = validateFilePath(filePath);
    if (!validated) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "Only Markdown file paths (.md, .markdown, .mdx, …) are allowed.",
        ),
      };
    }
    filePath = validated;
  }

  const projectPath = options.projectPath ?? `${owner}/${repo}`;
  if (projectPath.length > MAX_PATH_LEN || projectPath.includes("..")) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "Project path is invalid."),
    };
  }

  return {
    ok: true,
    value: {
      provider,
      owner,
      repo,
      projectPath,
      hubKind: options.hubKind ?? null,
      ref,
      filePath,
    },
  };
}

function parseGithub(
  hostname: string,
  segments: string[],
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (githubRawHosts.has(hostname)) {
    // raw.githubusercontent.com/{owner}/{repo}/{ref}/{path…}
    if (segments.length < 4) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "raw.githubusercontent.com URLs must include owner, repo, ref, and file path.",
        ),
      };
    }
    const [owner, repo, ref, ...pathParts] = segments;
    return parsed("github", owner, stripGitSuffix(repo), {
      ref,
      filePath: joinPath(pathParts),
    });
  }

  if (!githubBrowseHosts.has(hostname)) {
    return {
      ok: false,
      error: policyError(
        "UNSUPPORTED_HOST",
        "Use a public github.com or raw.githubusercontent.com URL.",
      ),
    };
  }

  if (segments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "GitHub URLs must look like https://github.com/owner/repo.",
      ),
    };
  }

  const owner = segments[0];
  const repo = stripGitSuffix(segments[1]);

  if (segments.length === 2) {
    return parsed("github", owner, repo);
  }

  const kind = segments[2];
  if (kind === "tree" || kind === "blob" || kind === "raw") {
    if (segments.length < 4) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "GitHub blob/raw/tree URLs must include a ref.",
        ),
      };
    }
    const ref = segments[3];
    const pathParts = segments.slice(4);
    if (kind === "tree" && pathParts.length === 0) {
      return parsed("github", owner, repo, { ref });
    }
    if (pathParts.length === 0) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "GitHub blob/raw URLs must include a Markdown file path.",
        ),
      };
    }
    return parsed("github", owner, repo, {
      ref,
      filePath: joinPath(pathParts),
    });
  }

  return {
    ok: false,
    error: policyError(
      "INVALID_URL",
      "Unsupported GitHub URL path. Use a repository or Markdown file URL.",
    ),
  };
}

function parseGitlab(segments: string[]): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  const dashIndex = segments.indexOf("-");
  const projectSegments =
    dashIndex >= 0 ? segments.slice(0, dashIndex) : segments;

  if (projectSegments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "GitLab URLs must look like https://gitlab.com/group/project.",
      ),
    };
  }

  const repo = stripGitSuffix(projectSegments[projectSegments.length - 1]);
  const owner = projectSegments[0];
  const projectPath = [...projectSegments.slice(0, -1), repo].join("/");

  if (dashIndex < 0) {
    return parsed("gitlab", owner, repo, { projectPath });
  }

  const after = segments.slice(dashIndex + 1);
  // /-/blob|raw/{ref}/{path} or /-/tree/{ref}
  if (after.length === 0) {
    return parsed("gitlab", owner, repo, { projectPath });
  }

  const kind = after[0];
  if (kind !== "blob" && kind !== "raw" && kind !== "tree") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Unsupported GitLab URL path. Use a repository or Markdown file URL.",
      ),
    };
  }

  if (after.length < 2) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "GitLab blob/raw/tree URLs must include a ref."),
    };
  }

  const ref = after[1];
  const pathParts = after.slice(2);

  if (kind === "tree" && pathParts.length === 0) {
    return parsed("gitlab", owner, repo, { projectPath, ref });
  }

  if (pathParts.length === 0) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "GitLab blob/raw URLs must include a Markdown file path.",
      ),
    };
  }

  return parsed("gitlab", owner, repo, {
    projectPath,
    ref,
    filePath: joinPath(pathParts),
  });
}

function parseBitbucket(
  segments: string[],
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (segments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Bitbucket URLs must look like https://bitbucket.org/workspace/repo.",
      ),
    };
  }

  const owner = segments[0];
  const repo = stripGitSuffix(segments[1]);

  if (segments.length === 2) {
    return parsed("bitbucket", owner, repo);
  }

  if (segments[2] !== "src") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Unsupported Bitbucket URL path. Use a repository or /src/ Markdown URL.",
      ),
    };
  }

  if (segments.length < 4) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Bitbucket src URLs must include a ref.",
      ),
    };
  }

  const ref = segments[3];
  const pathParts = segments.slice(4);
  if (pathParts.length === 0) {
    return parsed("bitbucket", owner, repo, { ref });
  }

  return parsed("bitbucket", owner, repo, {
    ref,
    filePath: joinPath(pathParts),
  });
}

function parseGiteaLike(
  provider: "codeberg" | "gitea",
  segments: string[],
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (segments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        `URLs must look like https://${provider === "codeberg" ? "codeberg.org" : "gitea.com"}/owner/repo.`,
      ),
    };
  }

  const owner = segments[0];
  const repo = stripGitSuffix(segments[1]);

  if (segments.length === 2) {
    return parsed(provider, owner, repo);
  }

  // /src|raw/{branch|commit|tag}/{ref}/{path…}
  const kind = segments[2];
  if (kind !== "src" && kind !== "raw") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Unsupported forge URL path. Use a repository or Markdown file URL.",
      ),
    };
  }

  if (segments.length < 5) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Forge src/raw URLs must include object type, ref, and optional path.",
      ),
    };
  }

  const objectType = segments[3];
  if (
    objectType !== "branch" &&
    objectType !== "commit" &&
    objectType !== "tag"
  ) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Forge src/raw URLs must use branch, commit, or tag.",
      ),
    };
  }

  const ref = segments[4];
  const pathParts = segments.slice(5);
  if (kind === "src" && pathParts.length === 0) {
    return parsed(provider, owner, repo, { ref });
  }
  if (pathParts.length === 0) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Forge raw URLs must include a Markdown file path.",
      ),
    };
  }

  return parsed(provider, owner, repo, {
    ref,
    filePath: joinPath(pathParts),
  });
}

function parseSourcehut(
  segments: string[],
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  // /~user/repo …
  if (segments.length < 2 || !segments[0].startsWith("~")) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "SourceHut URLs must look like https://git.sr.ht/~user/repo.",
      ),
    };
  }

  const owner = segments[0];
  const repo = stripGitSuffix(segments[1]);

  if (segments.length === 2) {
    return parsed("sourcehut", owner, repo);
  }

  const kind = segments[2];
  if (kind === "tree") {
    // /tree/{ref}/item/{path…} or /tree/{ref}
    if (segments.length < 4) {
      return {
        ok: false,
        error: policyError("INVALID_URL", "SourceHut tree URLs must include a ref."),
      };
    }
    const ref = segments[3];
    if (segments.length === 4) {
      return parsed("sourcehut", owner, repo, { ref });
    }
    if (segments[4] !== "item" || segments.length < 6) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "SourceHut tree file URLs must look like …/tree/{ref}/item/{path}.",
        ),
      };
    }
    return parsed("sourcehut", owner, repo, {
      ref,
      filePath: joinPath(segments.slice(5)),
    });
  }

  if (kind === "blob") {
    // /blob/{ref}/{path…}
    if (segments.length < 5) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "SourceHut blob URLs must include a ref and Markdown path.",
        ),
      };
    }
    return parsed("sourcehut", owner, repo, {
      ref: segments[3],
      filePath: joinPath(segments.slice(4)),
    });
  }

  return {
    ok: false,
    error: policyError(
      "INVALID_URL",
      "Unsupported SourceHut URL path. Use a repository or Markdown file URL.",
    ),
  };
}

function parseHuggingFace(
  segments: string[],
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (segments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Hugging Face URLs must look like https://huggingface.co/owner/repo.",
      ),
    };
  }

  // Optional datasets/|spaces/ prefix
  let offset = 0;
  let hubKind: "datasets" | "spaces" | null = null;
  if (segments[0] === "datasets" || segments[0] === "spaces") {
    hubKind = segments[0];
    offset = 1;
  }

  if (segments.length < offset + 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Hugging Face URLs must include owner and repo.",
      ),
    };
  }

  const owner = segments[offset];
  const repo = stripGitSuffix(segments[offset + 1]);
  const rest = segments.slice(offset + 2);

  if (rest.length === 0) {
    return parsed("huggingface", owner, repo, { hubKind });
  }

  const kind = rest[0];
  if (kind !== "blob" && kind !== "raw" && kind !== "resolve" && kind !== "tree") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Unsupported Hugging Face URL path. Use a repository or Markdown file URL.",
      ),
    };
  }

  if (rest.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Hugging Face blob/raw/resolve URLs must include a ref.",
      ),
    };
  }

  const ref = rest[1];
  const pathParts = rest.slice(2);
  if ((kind === "tree" || kind === "blob") && pathParts.length === 0) {
    return parsed("huggingface", owner, repo, { hubKind, ref });
  }
  if (pathParts.length === 0) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Hugging Face file URLs must include a Markdown path.",
      ),
    };
  }

  return parsed("huggingface", owner, repo, {
    hubKind,
    ref,
    filePath: joinPath(pathParts),
  });
}

function parseGitee(segments: string[]): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (segments.length < 2) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Gitee URLs must look like https://gitee.com/owner/repo.",
      ),
    };
  }

  const owner = segments[0];
  const repo = stripGitSuffix(segments[1]);

  if (segments.length === 2) {
    return parsed("gitee", owner, repo);
  }

  const kind = segments[2];
  if (kind !== "blob" && kind !== "raw" && kind !== "tree") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Unsupported Gitee URL path. Use a repository or Markdown file URL.",
      ),
    };
  }

  if (segments.length < 4) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "Gitee blob/raw/tree URLs must include a ref."),
    };
  }

  const ref = segments[3];
  const pathParts = segments.slice(4);
  if (kind === "tree" && pathParts.length === 0) {
    return parsed("gitee", owner, repo, { ref });
  }
  if (pathParts.length === 0) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Gitee blob/raw URLs must include a Markdown file path.",
      ),
    };
  }

  return parsed("gitee", owner, repo, {
    ref,
    filePath: joinPath(pathParts),
  });
}

function parseAzureDevOps(
  segments: string[],
  searchParams: URLSearchParams,
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  // /{org}/{project}/_git/{repo}
  if (segments.length < 4 || segments[2] !== "_git") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Azure DevOps URLs must look like https://dev.azure.com/{org}/{project}/_git/{repo}.",
      ),
    };
  }

  const owner = segments[0];
  const project = segments[1];
  const repo = stripGitSuffix(segments[3]);
  const projectPath = `${owner}/${project}/${repo}`;

  let ref: string | null = null;
  const version = searchParams.get("version");
  if (version) {
    // GB{branch}, GT{tag}, GC{commit}
    const match = /^(?:GB|GT|GC)(.+)$/.exec(version);
    if (!match || !isValidSegment(match[1], MAX_REF_LEN)) {
      return {
        ok: false,
        error: policyError("INVALID_URL", "Azure DevOps version parameter is invalid."),
      };
    }
    ref = match[1];
  }

  const pathParam = searchParams.get("path");
  if (!pathParam || pathParam === "/" || pathParam === "") {
    return parsed("azuredevops", owner, repo, { projectPath, ref });
  }

  const normalized = pathParam.replace(/^\/+/, "");
  return parsed("azuredevops", owner, repo, {
    projectPath,
    ref,
    filePath: normalized,
  });
}

/**
 * Validate and normalize a public code-host repository or Markdown file URL.
 * Does not perform network I/O. API hosts (api.github.com, api.bitbucket.org)
 * are rejected as user input — they are only used after canonicalize at fetch time.
 */
export function parseAllowedRepoMarkdownUrl(
  rawUrl: string,
): RepoMarkdownResult<ParsedRepoMarkdownUrl> {
  if (typeof rawUrl !== "string" || rawUrl.trim().length === 0) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "A repository or Markdown URL is required."),
    };
  }

  let url: URL;

  try {
    url = new URL(rawUrl.trim());
  } catch {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "URL is not a valid absolute URL.",
      ),
    };
  }

  if (url.protocol !== "https:") {
    return {
      ok: false,
      error: policyError("INVALID_URL", "Only HTTPS repository URLs are allowed."),
    };
  }

  if (url.username || url.password) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Repository URLs must not include credentials.",
      ),
    };
  }

  const hostname = normalizeHostname(url.hostname);
  const segments = url.pathname.split("/").filter(Boolean);

  if (githubBrowseHosts.has(hostname) || githubRawHosts.has(hostname)) {
    return parseGithub(hostname, segments);
  }
  if (gitlabHosts.has(hostname)) {
    return parseGitlab(segments);
  }
  if (bitbucketHosts.has(hostname)) {
    return parseBitbucket(segments);
  }
  if (codebergHosts.has(hostname)) {
    return parseGiteaLike("codeberg", segments);
  }
  if (giteaHosts.has(hostname)) {
    return parseGiteaLike("gitea", segments);
  }
  if (sourcehutHosts.has(hostname)) {
    return parseSourcehut(segments);
  }
  if (huggingfaceHosts.has(hostname)) {
    return parseHuggingFace(segments);
  }
  if (giteeHosts.has(hostname)) {
    return parseGitee(segments);
  }
  if (azureHosts.has(hostname)) {
    return parseAzureDevOps(segments, url.searchParams);
  }

  return {
    ok: false,
    error: policyError(
      "UNSUPPORTED_HOST",
      "Use a public URL from GitHub, GitLab, Bitbucket, Codeberg, Gitea.com, SourceHut, Hugging Face, Gitee, or Azure DevOps.",
    ),
  };
}

export function isRepoMarkdownImportQuery(
  value: unknown,
): value is "readme" {
  return value === "readme";
}
